import type { Message } from "@langchain/langgraph-sdk";

export const CONTEXT_MESSAGE_NAME = "context";

/**
 * True for messages that exist purely for the agent's eye — system
 * reminders, inbox context notes, deploy events, etc. These should
 * never render as a user/agent turn in the visible transcript.
 *
 * Two complementary signals:
 *   1. `msg.name === "context"` — the canonical tag set by senders.
 *   2. `msg.additional_kwargs.reminderType` is a non-empty string — set
 *      by every SystemReminder helper. Belt-and-suspenders against
 *      `name` getting stripped somewhere in the SDK serialization
 *      path. If a sender forgets `name=context` but tags `reminderType`,
 *      we still treat it as context.
 */
export function isContextMessage(msg: Message): boolean {
  if (msg.name === CONTEXT_MESSAGE_NAME) return true;
  const ak = (msg as { additional_kwargs?: Record<string, unknown> })
    .additional_kwargs;
  if (ak && typeof ak.reminderType === "string" && ak.reminderType.length > 0) {
    return true;
  }
  return false;
}

export function filterContextMessages<T extends Message>(messages: T[]): T[] {
  return messages.filter((msg) => !isContextMessage(msg));
}

/**
 * True when a message has nothing for ANY consumer to render — no text,
 * no tool calls, no content blocks of any kind.
 *
 * Anthropic occasionally returns end_turn assistant turns with `content: []`
 * and zero tool_calls (e.g. after a completed tool round-trip where the
 * model had nothing more to add). These get persisted to thread state,
 * re-render as empty bubbles on history reload, and flicker in during
 * streaming before any tokens arrive. Drop them at the stream boundary
 * so every consumer is shielded from junk.
 *
 * Stay maximally lenient here. Tool-only AI messages (content empty,
 * tool_calls populated) are NOT junk — `Chat.AIMessage` uses them to
 * render the `ToolCallIndicator` (e.g. brainstorm's "Saving answers…"
 * pill). Filtering tool-only messages at the stream layer would silently
 * hide that indicator across every consumer. The "should we render an
 * empty AI bubble for a tool-only message?" question is product-specific
 * and belongs in the renderer (see AdsChatMessages, which has no tool
 * indicator UI and skips them; brainstorm, which renders the indicator).
 */
export function isEmptyMessage(msg: Message): boolean {
  const tc = (msg as any).tool_calls;
  if (Array.isArray(tc) && tc.length > 0) return false;

  const tcc = (msg as any).tool_call_chunks;
  if (Array.isArray(tcc) && tcc.length > 0) return false;

  const content = msg.content;
  if (typeof content === "string") return content.trim() === "";
  if (Array.isArray(content)) {
    if (content.length === 0) return true;
    return !content.some((block: any) => {
      if (!block || typeof block !== "object") return false;
      if (block.type === "text") return typeof block.text === "string" && block.text.trim().length > 0;
      // tool_use, image_url, document, reasoning — anything non-text-block
      // counts as non-empty so consumer renderers can decide what to do.
      return true;
    });
  }
  return content == null;
}

export function filterEmptyMessages<T extends Message>(messages: T[]): T[] {
  return messages.filter((msg) => !isEmptyMessage(msg));
}

/**
 * Collapse duplicate message ids down to one entry.
 *
 * With `streamSubgraphs: true` the same message can arrive under two
 * scopes mid-run and land in the array twice. Duplicate ids break React
 * keys (every consumer keys rows on `message.id`) and double-render
 * anything anchored to the message (tool-activity cards). Keep the
 * FIRST occurrence's position (chronology) with the LAST occurrence's
 * content (the later copy is the more complete one). Messages without
 * ids pass through untouched.
 */
export function dedupeMessagesById<T extends Message>(messages: T[]): T[] {
  const indexById = new Map<string, number>();
  const result: T[] = [];
  let hadDuplicate = false;

  for (const msg of messages) {
    const id = msg.id;
    if (!id) {
      result.push(msg);
      continue;
    }
    const existing = indexById.get(id);
    if (existing === undefined) {
      indexById.set(id, result.length);
      result.push(msg);
    } else {
      result[existing] = msg;
      hadDuplicate = true;
    }
  }

  // Preserve referential stability when there was nothing to do — the
  // stream syncs on every event and downstream memoization compares refs.
  return hadDuplicate ? result : messages;
}

/**
 * Map SDK role values to LangChain message types.
 * The SDK's MessageTupleManager.toMessageDict() can produce messages with
 * { type: "message", role: "assistant" } instead of { type: "ai" }.
 * This normalizes them so downstream code can rely on standard types.
 */
const ROLE_TO_TYPE: Record<string, string> = {
  assistant: "ai",
  human: "human",
  user: "human",
  system: "system",
  tool: "tool",
};

export function normalizeMessageTypes<T extends Message>(messages: T[]): T[] {
  return messages.map((msg) => {
    const role = (msg as any).role as string | undefined;
    if (role && ROLE_TO_TYPE[role] && msg.type !== ROLE_TO_TYPE[role]) {
      return { ...msg, type: ROLE_TO_TYPE[role] } as T;
    }
    return msg;
  });
}
